// src/ryan/utils/ryanAnalytics.ts
//
// Captura de eventos de comportamento anônimo em RyanProductPage/
// RyanCheckoutPage (Fase 1 do analytics de funil — só captura, sem UI de
// leitura ainda). Todo disparo é best-effort e nunca bloqueia a página:
// qualquer falha (storage indisponível, rede fora, endpoint fora do ar) é
// engolida em silêncio — mesma postura do tracking anônimo que já existe
// nessas duas páginas (ver ryan_ref_${slug}).
//
// De propósito NÃO coleta IP nem user-agent (decisão de LGPD) — só os dois
// IDs anônimos abaixo e, a partir da autenticação, o userId já salvo em
// localStorage pelo AuthContext. O código de embaixador (referralCode) É
// capturado (ver getReferralCode abaixo) desde que passou a existir um uso
// agregado legítimo pra ele (funil/comportamento por embaixador) — mas o
// design que impedia reidentificação da TIMELINE INDIVIDUAL continua de pé
// por outro caminho: o backend nunca aceita nem devolve referralCode nos
// endpoints de sessão (GET .../sessions e .../sessions/:sessionId), só nos
// agregados (GET .../funnel e .../behavior). Ver comentário em
// RyanAnalyticsEvent.js e nos dois handlers de sessão em ryanRoutes.js.

export type RyanAnalyticsPage = 'product' | 'checkout';

export type RyanAnalyticsEventType =
    | 'pageview' | 'scroll_depth' | 'click_preview' | 'click_video_play'
    | 'video_watch_progress' | 'click_buy' | 'checkout_started'
    | 'checkout_step' | 'payment_approved' | 'checkout_abandoned';

interface QueuedEvent {
    productId: string;
    visitorId: string;
    sessionId: string;
    userId: string | null;
    page: RyanAnalyticsPage;
    eventType: RyanAnalyticsEventType;
    source: 'direct' | 'referral';
    referralCode: string | null;
    metadata: Record<string, unknown>;
}

// Chave única de opt-out de analytics — checada antes de QUALQUER evento
// entrar na fila. Guarda 'true'/'false' explicitamente (nunca remove a
// chave), pra RyanAdminPage.tsx distinguir "nunca decidido" (chave ausente,
// aplica o padrão automático uma vez) de "usuário já escolheu antes" (chave
// presente, nunca mais sobrescrever sozinho) — ver isOptOutDecided().
const OPTOUT_KEY = 'ryan_analytics_optout';

export function isOptedOut(): boolean {
    try {
        return localStorage.getItem(OPTOUT_KEY) === 'true';
    } catch {
        return false;
    }
}

// true assim que QUALQUER lado (automático ou manual) já gravou uma escolha
// nesse navegador — usado só pelo auto-default em RyanAdminPage.tsx pra
// nunca reaplicar o padrão por cima de uma escolha explícita.
export function isOptOutDecided(): boolean {
    try {
        return localStorage.getItem(OPTOUT_KEY) !== null;
    } catch {
        return true; // sem storage, não há nada pra "decidir" — trata como resolvido
    }
}

export function setOptedOut(value: boolean): void {
    try {
        localStorage.setItem(OPTOUT_KEY, value ? 'true' : 'false');
    } catch {
        /* localStorage indisponível — segue sem persistir a escolha */
    }
}

const TRACK_ENDPOINT = '/api/ryan/analytics/track';
const FLUSH_INTERVAL_MS = 5000;
// Mesmo TTL de ryan_ref_${slug} já usado pro código de embaixador — aqui só
// determina se ainda vale como "veio de indicação", nunca lê o código em si.
const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let globalListenersReady = false;

// Não precisa ser criptograficamente forte, só razoavelmente único — permite
// funcionar em navegadores sem crypto.randomUUID (ex: Safari < 15.4).
function generateId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// Visitante único: sem expiração, sobrevive a fechar a aba/o navegador.
function getVisitorId(): string {
    try {
        const KEY = 'ryan_visitor_id';
        let id = localStorage.getItem(KEY);
        if (!id) {
            id = generateId();
            localStorage.setItem(KEY, id);
        }
        return id;
    } catch {
        return 'no-storage';
    }
}

// Sessão/visita: sessionStorage, então reseta por aba — duas abas abertas
// pela mesma pessoa contam como duas sessões (mesmo comportamento do GA).
function getSessionId(): string {
    try {
        const KEY = 'ryan_session_id';
        let id = sessionStorage.getItem(KEY);
        if (!id) {
            id = generateId();
            sessionStorage.setItem(KEY, id);
        }
        return id;
    } catch {
        return 'no-storage';
    }
}

// Só interessa SE existe indicação válida, não qual é o código — usada por
// getTrafficSource() pra decidir 'direct' vs 'referral'. getReferralCode()
// logo abaixo é que lê o código em si (mesma fonte, mesmo TTL).
function hasStoredReferral(slug: string): boolean {
    try {
        const raw = localStorage.getItem(`ryan_ref_${slug}`);
        if (!raw) return false;
        const { code, savedAt } = JSON.parse(raw);
        return !!code && Date.now() - savedAt <= REFERRAL_TTL_MS;
    } catch {
        return false;
    }
}

// Lê o código de embaixador em si — mesma fonte (?rep= na URL ou
// ryan_ref_${slug} no localStorage, mesmo TTL de 30 dias) que
// RyanProductPage.tsx/RyanCheckoutPage.tsx já usavam localmente antes desta
// função virar a fonte única (evita as duas cópias divergirem um dia e a
// classificação "veio de X" ficar inconsistente entre comissão e analytics).
// Só a URL não basta pelo mesmo motivo de getTrafficSource: o checkout não
// repassa ?rep= ao navegar a partir da página do produto.
export function getReferralCode(slug: string): string | null {
    try {
        const urlCode = new URLSearchParams(window.location.search).get('rep');
        if (urlCode) return urlCode;
        const raw = localStorage.getItem(`ryan_ref_${slug}`);
        if (!raw) return null;
        const { code, savedAt } = JSON.parse(raw);
        if (!code || Date.now() - savedAt > REFERRAL_TTL_MS) {
            localStorage.removeItem(`ryan_ref_${slug}`);
            return null;
        }
        return code;
    } catch {
        return null;
    }
}

// Calculado a cada evento (não cacheado por sessão): a página de checkout não
// repassa ?rep= na URL ao navegar por clique a partir da página do produto
// (ver rpp-hero-cta), então só ler a URL da página atual classificaria
// incorretamente uma sessão inteira vinda de indicação como 'direct' assim
// que ela chega no checkout — por isso o fallback no localStorage.
function getTrafficSource(slug: string): 'direct' | 'referral' {
    try {
        const urlHasRep = new URLSearchParams(window.location.search).get('rep');
        return urlHasRep || hasStoredReferral(slug) ? 'referral' : 'direct';
    } catch {
        return 'direct';
    }
}

// Mesma chave 'userId' que AuthContext já grava no login — não decodifica
// token nem duplica lógica de auth aqui.
function getAuthenticatedUserId(): string | null {
    try {
        return localStorage.getItem('userId');
    } catch {
        return null;
    }
}

function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
        flushTimer = null;
        flush(false);
    }, FLUSH_INTERVAL_MS);
}

function flush(useBeacon: boolean) {
    if (queue.length === 0) return;
    const batch = queue;
    queue = [];
    const body = JSON.stringify({ events: batch });
    try {
        if (useBeacon && navigator.sendBeacon) {
            navigator.sendBeacon(TRACK_ENDPOINT, new Blob([body], { type: 'application/json' }));
        } else {
            fetch(TRACK_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body,
                keepalive: true,
            }).catch(() => { /* best-effort — perder um batch nunca deve afetar a navegação */ });
        }
    } catch {
        /* sendBeacon/fetch indisponível — segue sem rastrear este batch */
    }
}

// Registrado uma única vez (guard global, não por tracker) — pagehide cobre
// fechar a aba/navegar pra fora; visibilitychange (hidden) cobre trocar de
// app no mobile sem fechar a aba. sendBeacon nos dois porque a partir daqui a
// página pode nunca mais rodar JS (um fetch normal seria cancelado).
function ensureGlobalListeners() {
    if (globalListenersReady || typeof window === 'undefined') return;
    globalListenersReady = true;
    window.addEventListener('pagehide', () => flush(true));
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush(true);
    });
}

// Força o flush imediato da fila via sendBeacon — usado por quem precisa
// garantir que um evento específico (ex: checkout_abandoned) saia ANTES da
// página descarregar, sem depender da ordem entre o listener de pagehide
// próprio da página e o listener global registrado por ensureGlobalListeners
// (dois listeners do mesmo evento em elementos diferentes não têm ordem
// garantida entre si de forma óbvia de se raciocinar — mais simples cada
// chamador cuidar do próprio flush explicitamente).
export function flushRyanAnalytics() {
    flush(true);
}

export interface RyanTracker {
    track: (eventType: RyanAnalyticsEventType, metadata?: Record<string, unknown>) => void;
}

// Uma "tracker" por (produto, página) — sem estado próprio entre chamadas,
// então pode ser recriada a qualquer momento (ex: dentro de cada handler de
// clique) sem custo real: só fecha sobre productId/slug/page já disponíveis
// no escopo de quem chama.
//
// track() monta o evento e empilha de forma SÍNCRONA de propósito (só alguns
// reads de storage + push num array — microssegundos, não compete com nada) —
// nunca adiei isso via requestIdleCallback/setTimeout: o evento
// 'checkout_abandoned' é enfileirado de dentro de um listener de pagehide, e
// qualquer callback adiado ali corre o risco real de nunca chegar a rodar
// antes da página descarregar. O custo caro (a chamada de rede) já está
// isolado em flush(), que sim é batched/adiado (ver scheduleFlush).
export function makeRyanTracker(productId: string, slug: string, page: RyanAnalyticsPage): RyanTracker {
    ensureGlobalListeners();
    return {
        track(eventType, metadata = {}) {
            if (isOptedOut()) return;
            try {
                queue.push({
                    productId,
                    visitorId: getVisitorId(),
                    sessionId: getSessionId(),
                    userId: getAuthenticatedUserId(),
                    page,
                    eventType,
                    source: getTrafficSource(slug),
                    referralCode: getReferralCode(slug),
                    metadata,
                });
                scheduleFlush();
            } catch {
                /* nunca deixa uma falha de tracking afetar a página real */
            }
        },
    };
}
